import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { OfferLineScopeItem } from '../services/OfferService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface OfferLineScopeDrawerProps {
  offerLineId: number;
  lineName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function OfferLineScopeDrawer({
  offerLineId,
  lineName,
  isOpen,
  onClose,
}: OfferLineScopeDrawerProps): React.JSX.Element | null {
  const [scopeItems, setScopeItems] = useState<OfferLineScopeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const loadScopeItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OfferService.fetchOfferLineScopeItems(offerLineId);
      setScopeItems(response.scope_items);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load scope items:', err);
      setScopeItems([]);
    } finally {
      setLoading(false);
    }
  }, [offerLineId]);

  useEffect(() => {
    if (isOpen) {
      loadScopeItems();
    }
  }, [isOpen, loadScopeItems]);

  const handleAddItem = () => {
    const maxSortOrder = scopeItems.length > 0
      ? Math.max(...scopeItems.map((s) => s.sort_order))
      : 0;

    setScopeItems((prev) => [
      ...prev,
      { text: '', sort_order: maxSortOrder + 10 },
    ]);
    setHasChanges(true);
  };

  const handleUpdateText = (index: number, newText: string) => {
    setScopeItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, text: newText } : item))
    );
    setHasChanges(true);
  };

  const handleDelete = (index: number) => {
    setScopeItems((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...scopeItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    // Recalculate sort_order
    newItems.forEach((item, i) => {
      item.sort_order = (i + 1) * 10;
    });
    setScopeItems(newItems);
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === scopeItems.length - 1) return;
    const newItems = [...scopeItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    // Recalculate sort_order
    newItems.forEach((item, i) => {
      item.sort_order = (i + 1) * 10;
    });
    setScopeItems(newItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Filter out empty items
    const validItems = scopeItems
      .filter((item) => item.text.trim())
      .map((item, index) => ({
        text: item.text.trim(),
        sort_order: (index + 1) * 10,
      }));

    setSaving(true);
    try {
      const response = await OfferService.updateOfferLineScopeItems(offerLineId, validItems);
      setScopeItems(response.scope_items);
      setHasChanges(false);
      toast.success('Leistungsumfang gespeichert');
    } catch (err: any) {
      console.error('Failed to save scope items:', err);
      const msg = err?.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="offer-line-scope-drawer"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '450px',
        background: colors.bgCard,
        boxShadow: shadows.elevated,
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.navy }}>
            Leistungsumfang (Snapshot)
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: colors.textMuted }}>
            {lineName}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            color: colors.textMuted,
          }}
        >
          <FaTimes size={18} />
        </button>
      </div>

      {/* Info Banner */}
      <div
        style={{
          padding: '12px 24px',
          background: '#fefce8',
          borderBottom: '1px solid #fef08a',
          fontSize: '12px',
          color: '#854d0e',
        }}
      >
        Snapshot aus Katalog - Änderungen hier betreffen nur dieses Angebot.
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {loading ? (
          <p style={{ color: colors.textLight, fontSize: '13px' }}>Laden...</p>
        ) : scopeItems.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              color: colors.textLight,
              padding: '16px',
              background: colors.bgMuted,
              borderRadius: '6px',
              textAlign: 'center',
            }}
          >
            Noch keine Scope-Punkte. Füge Punkte hinzu, die als Checkliste in den Jobs erscheinen.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scopeItems.map((item, index) => (
              <div
                key={index}
                data-testid={`scope-item-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  background: colors.bgMuted,
                  borderRadius: '6px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    style={{
                      ...arrowButtonStyle,
                      opacity: index === 0 ? 0.3 : 1,
                    }}
                    title="Nach oben"
                  >
                    <FaArrowUp size={10} />
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === scopeItems.length - 1}
                    style={{
                      ...arrowButtonStyle,
                      opacity: index === scopeItems.length - 1 ? 0.3 : 1,
                    }}
                    title="Nach unten"
                  >
                    <FaArrowDown size={10} />
                  </button>
                </div>

                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => handleUpdateText(index, e.target.value)}
                  placeholder="Beschreibung..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />

                <button
                  data-testid={`scope-delete-${index}`}
                  onClick={() => handleDelete(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    color: colors.danger,
                  }}
                  title="Löschen"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          data-testid="add-scope-item"
          onClick={handleAddItem}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 16px',
            marginTop: '16px',
            border: `1px dashed ${colors.border}`,
            borderRadius: '10px',
            background: colors.bgCard,
            color: colors.textMuted,
            fontSize: '13px',
            cursor: 'pointer',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <FaPlus size={12} />
          <span>Punkt hinzufügen</span>
        </button>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 24px',
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <button
          onClick={onClose}
          style={{
            ...ps.buttonSecondary,
          }}
        >
          Schließen
        </button>
        <button
          data-testid="save-scope-items"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          style={{
            ...(saving || !hasChanges ? ps.buttonDisabled : ps.buttonPrimary),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <FaSave size={12} />
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

const arrowButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  color: colors.textMuted,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
